import * as React from "react";

import type {
	BrandSettings,
	DomainSettings,
	LocalisationSettings,
	Settings,
	WhitelabelSettings,
} from "~mangomap-ui/types/settings";
import { DEFAULT_LOCALISATION_SETTINGS } from "~mangomap-ui/types/settings";

export interface SettingsContextValue {
	settings: Settings;
	setSettings: React.Dispatch<React.SetStateAction<Settings>>;
}

export const defaultSettings: Settings = {
	primaryColor: "mango",
	direction: "ltr",
};

export const SettingsContext = React.createContext<SettingsContextValue>({
	settings: {},
	setSettings: () => undefined,
});

export interface SettingsProviderProps {
	children: React.ReactNode;
	settings: Settings;
}

export function SettingsProvider({
	children,
	settings: initialSettings,
}: SettingsProviderProps): React.JSX.Element {
	const [settings, setSettings] = React.useState<Settings>(initialSettings);

	React.useEffect(() => {
		setSettings(initialSettings);
	}, [initialSettings]);

	return (
		<SettingsContext.Provider value={{ settings, setSettings }}>
			{children}
		</SettingsContext.Provider>
	);
}

export const SettingsConsumer = SettingsContext.Consumer;

export function useSettings(): SettingsContextValue {
	const context = React.useContext(SettingsContext);

	if (!context) {
		throw new Error("useSettings must be used within a SettingsProvider");
	}

	return context;
}

export function useBrandSettings(): {
	brandSettings: BrandSettings;
	setBrandSettings: (brandSettings: BrandSettings) => void;
} {
	const { settings, setSettings } = useSettings();

	const setBrandSettings = React.useCallback(
		(brandSettings: BrandSettings) => {
			setSettings((prevSettings) => ({
				...prevSettings,
				brand: brandSettings,
			}));
		},
		[setSettings],
	);

	return {
		brandSettings: settings.brand ?? {},
		setBrandSettings,
	};
}

export function useLocalisationSettings(): {
	localisationSettings: LocalisationSettings;
	setLocalisationSettings: (localisationSettings: LocalisationSettings) => void;
} {
	const { settings, setSettings } = useSettings();

	const setLocalisationSettings = React.useCallback(
		(localisationSettings: LocalisationSettings) => {
			setSettings((prev) => ({
				...prev,
				localisation: localisationSettings,
			}));
		},
		[setSettings],
	);

	return {
		localisationSettings:
			settings.localisation ?? DEFAULT_LOCALISATION_SETTINGS,
		setLocalisationSettings,
	};
}

export function useDomainSettings(): {
	domainSettings: DomainSettings;
	setDomainSettings: (domainSettings: DomainSettings) => void;
} {
	const { settings, setSettings } = useSettings();

	const setDomainSettings = React.useCallback(
		(domainSettings: DomainSettings) => {
			setSettings((prevSettings) => ({
				...prevSettings,
				domain: domainSettings,
			}));
		},
		[setSettings],
	);

	return {
		domainSettings: settings.domain ?? {},
		setDomainSettings,
	};
}

export function useWhitelabelSettings(): {
	whitelabelSettings: WhitelabelSettings;
	setWhitelabelSettings: (whitelabelSettings: WhitelabelSettings) => void;
} {
	const { settings, setSettings } = useSettings();

	const setWhitelabelSettings = React.useCallback(
		(whitelabelSettings: WhitelabelSettings) => {
			setSettings((prevSettings) => ({
				...prevSettings,
				whitelabel: whitelabelSettings,
			}));
		},
		[setSettings],
	);

	return {
		whitelabelSettings: settings.whitelabel ?? {},
		setWhitelabelSettings,
	};
}
